import { memo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Building2, Plus, Sparkles, type LucideIcon } from 'lucide-react';
import {
  QUICK_PICK_SHOWN,
  mergeQuickPicks,
  type PhraseLists,
  type QuickPickChoice,
  type QuickPickField,
  type QuickPickSource,
  type QuickPicksResponse,
} from '@his/shared';
import { get, qs } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { tr } from '@/lib/i18n-core';
import { cn } from '@/lib/utils';
import { useEncounterDepartment } from './completion';

const COMPLAINTS = ['fever', 'cough', 'headache', 'abdominal_pain', 'sore_throat', 'body_aches', 'vomiting', 'loose_stools', 'breathlessness', 'chest_pain'];

export const QUICK_PICK_DEFAULTS: Readonly<Record<QuickPickField, readonly string[]>> = {
  chiefComplaint: COMPLAINTS,
  presentingComplaints: COMPLAINTS,
  historyOfPresentIllness: ['sudden_onset', 'gradual_onset', 'intermittent', 'progressively_worse', 'relieved_by_rest', 'no_similar_episodes'],
  pastHistory: ['no_past_history', 'hypertension', 'diabetes', 'asthma', 'heart_disease', 'tb_treated'],
  surgicalHistory: ['no_surgery', 'appendectomy', 'caesarean_section', 'cholecystectomy'],
  familyHistory: ['no_family_history', 'family_diabetes', 'family_hypertension', 'family_heart_disease'],
  socialHistory: ['non_smoker', 'smoker', 'no_alcohol', 'lives_with_family'],
  drugHistory: ['no_regular_medicines', 'on_antihypertensives', 'on_diabetes_medicines', 'on_inhalers'],
  reviewOfSystems: ['no_other_complaints', 'no_weight_loss', 'no_night_sweats', 'no_chest_pain', 'no_breathlessness'],
  examination: ['conscious_oriented', 'no_pallor_jaundice', 'chest_clear', 'heart_sounds_normal', 'abdomen_soft', 'no_focal_deficit', 'throat_congested'],
  treatmentPlan: ['symptomatic_treatment', 'review_with_reports', 'continue_medicines', 'refer_specialist', 'admit_observation'],
  advice: ['plenty_of_fluids', 'rest_3_days', 'return_if_worse', 'medicines_after_meals', 'avoid_oily_food', 'follow_up_one_week'],
};

const NO_PHRASES: PhraseLists = {};

export interface QuickPickSources {
  learned: PhraseLists;
  department: PhraseLists;
}

const NO_PICKS: QuickPickSources = { learned: NO_PHRASES, department: NO_PHRASES };

export function useQuickPicks(enabled: boolean, encounterId?: string): QuickPickSources {
  const { can } = useAuth();
  const allowed = enabled && can('encounter.write');
  const department = useEncounterDepartment(allowed ? encounterId : undefined);
  const departmentId = department.departmentId;
  const query = useQuery({
    queryKey: ['quick-picks', departmentId],
    queryFn: () => get<QuickPicksResponse>(`/emr/quick-picks${qs({ departmentId: departmentId ?? undefined })}`),
    enabled: allowed && department.ready,
    staleTime: 10 * 60_000,
  });
  if (!query.data) return NO_PICKS;
  return { learned: query.data.phrases ?? NO_PHRASES, department: query.data.department ?? NO_PHRASES };
}

export function quickPickChips(field: QuickPickField, learned: readonly string[] | undefined, department?: readonly string[]): QuickPickChoice[] {
  return mergeQuickPicks(
    department,
    learned,
    QUICK_PICK_DEFAULTS[field].map((key) => tr('opd', `qp_${key}`)),
  );
}

const CHIP_ICON: Record<QuickPickSource, LucideIcon> = {
  department: Building2,
  learned: Sparkles,
  default: Plus,
};

const CHIP_TITLE: Partial<Record<QuickPickSource, string>> = {
  department: 'qp_department_hint',
  learned: 'qp_learned_hint',
};

const keepFocus = (event: { preventDefault: () => void }) => event.preventDefault();

export const QuickPickChips = memo(function QuickPickChips({
  field,
  learned,
  department,
  onPick,
  className,
}: {
  field: QuickPickField;
  learned?: readonly string[];
  department?: readonly string[];
  onPick: (phrase: string) => void;
  className?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const chips = quickPickChips(field, learned, department);
  const shown = expanded ? chips : chips.slice(0, QUICK_PICK_SHOWN);
  const hidden = chips.length - shown.length;
  return (
    <div role="group" aria-label={tr('opd', 'qp_group_label')} className={cn('flex flex-wrap gap-1.5', className)}>
      {shown.map((chip) => {
        const Icon = CHIP_ICON[chip.source];
        const title = CHIP_TITLE[chip.source];
        return (
          <button
            key={chip.text}
            type="button"
            onMouseDown={keepFocus}
            onClick={() => onPick(chip.text)}
            title={title ? tr('opd', title) : undefined}
            className={cn(
              'inline-flex max-w-full items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs transition-colors focus-ring',
              chip.source === 'department' && 'border-info/30 bg-info/[0.06] text-info hover:bg-info/10',
              chip.source === 'learned' && 'border-primary/30 bg-primary/[0.06] text-primary hover:bg-primary/10',
              chip.source === 'default' && 'border-border bg-background text-foreground/80 hover:border-primary/40 hover:bg-muted',
            )}
          >
            <Icon className={cn('h-3 w-3 shrink-0', chip.source === 'default' && 'text-muted-foreground')} aria-hidden />
            <span className="truncate">{chip.text}</span>
          </button>
        );
      })}
      {hidden > 0 && (
        <button type="button" onClick={() => setExpanded(true)} className="rounded-full px-2 py-0.5 text-xs font-medium text-primary hover:underline focus-ring">
          {tr('opd', 'qp_more_x', { count: hidden })}
        </button>
      )}
    </div>
  );
});
